import {
  type ApolloLink,
  Observable,
  createHttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createTreatyClient } from '../client';
import { MAX_REFRESH_ATTEMPTS, showLoginDialog } from './common';

let refreshAttempts = 0;

export const createHttpApolloLink = (
  hasuraUrl: string,
  headers?: Record<string, string>,
): ApolloLink => {
  const httpLink = createHttpLink({
    uri: hasuraUrl,
    credentials: 'include',
  });

  const authLink = setContext((_, { headers: oldHeaders }) => {
    // get the authentication token from local storage if it exists
    const token = localStorage.getItem('accessToken');
    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...headers,
        ...oldHeaders,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  // Error handling link for token refresh
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (
        networkError?.message.includes('401') ||
        graphQLErrors?.some((error) => error.extensions?.code === 'invalid-jwt')
      ) {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          showLoginDialog();
          return;
        }

        if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          refreshAttempts = 0;
          showLoginDialog();
          return;
        }

        return new Observable((observer) => {
          createTreatyClient()
            .api.auth.refresh.post({ refreshToken })
            .then((response) => {
              if (response.data && response.status === 200) {
                refreshAttempts++;
                const { accessToken } = response.data.data;
                localStorage.setItem('accessToken', accessToken);

                const oldHeaders = operation.getContext().headers;
                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${accessToken}`,
                  },
                });

                forward(operation).subscribe(observer);
              } else {
                console.error('Token refresh failed:', response.error?.value);
                showLoginDialog();
                observer.error(graphQLErrors);
              }
            })
            .catch((error) => {
              console.error('Token refresh failed:', error);
              showLoginDialog();
              observer.error(error);
            });
        });
      }
    },
  );

  return from([errorLink, authLink, httpLink]);
};
